/* eslint-disable react/no-unstable-nested-components */
import { AngleLeft, AngleRight, VerticalEllipsis } from '@/client/components/icons/ContinuIcons';
import {
  Button,
  ButtonGroup,
  Center,
  Flex,
  HStack,
  IconButton,
  Image,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import {
  ColumnDef,
  OnChangeFn,
  PaginationState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ellipsisCellStyles, ellipsisHeaderStyles } from '@/client/utils/charts/tableStyleUtils';

import { DefaultTFuncReturn } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from 'react-use-size';

interface AssignmentsTableProps {
  columns: ColumnDef<any, any>[];
  data: any[] | undefined;
  columnVisibility?: { [key: string]: boolean };
  getRowId: (row: any) => string;
  pagination: PaginationState;
  setPagination: OnChangeFn<PaginationState>;
  totalResultCount: number;
  isLoading: boolean;
  rowSelection?: { [key: string]: boolean };
  setRowSelection?: (rowSelection: { [key: string]: boolean }) => void;
  noResultsText?: string | DefaultTFuncReturn;
  onRowClick?: (row: any) => void;
}

export default function AssignmentsTable({
  columns,
  data,
  columnVisibility = {},
  getRowId,
  pagination,
  setPagination,
  totalResultCount,
  isLoading,
  rowSelection = {},
  setRowSelection = () => {},
  noResultsText = undefined,
  onRowClick = () => {},
}: AssignmentsTableProps) {
  const { t } = useTranslation();
  const { height } = useWindowSize();

  const table = useReactTable({
    data: data ?? [],
    columns,
    getRowId,
    enableRowSelection: (row) => row.original.node.completionStatus !== 'UNASSIGNED',
    onRowSelectionChange: (updaterOrValue) => {
      if (typeof updaterOrValue === 'function') {
        const newSelection = updaterOrValue(rowSelection);
        setRowSelection(newSelection);
      } else {
        setRowSelection(updaterOrValue);
      }
    },
    pageCount: Math.ceil(totalResultCount / pagination.pageSize),
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination,
      rowSelection,
      columnVisibility,
    },
    initialState: {
      columnVisibility,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
  });

  const pagesArr = Array.from({ length: table.getPageCount() }).map((_, index) => ({
    label: `${index + 1}`,
    value: index,
  }));

  return (
    <>
      <TableContainer maxHeight={height - 300} overflowY="auto" position="relative">
        <Table variant="assignments">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      ...ellipsisHeaderStyles(header, false),
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>

          <Tbody>
            {isLoading ? (
              <Tr>
                <Td colSpan={columns.length}>
                  <Center>
                    <Spinner />
                  </Center>
                </Td>
              </Tr>
            ) : totalResultCount > 0 ? (
              table.getCoreRowModel().rows.map((row) => {
                const isUnassigned = row.original.node.completionStatus === 'UNASSIGNED';

                return (
                  <Tr
                    key={row.id}
                    aria-disabled={isUnassigned}
                    onClick={() => onRowClick(row.original.node)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Td
                        key={cell.id}
                        style={{
                          ...ellipsisCellStyles(cell, false),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    ))}
                  </Tr>
                );
              })
            ) : (
              <Tr>
                <Td
                  backgroundColor="neutral.0"
                  _hover={{ backgroundColor: 'neutral.0' }}
                  colSpan={columns.length}
                >
                  <Center backgroundColor="neutral.0" height={height - 550}>
                    <VStack spacing={4}>
                      <Image
                        src="/public/images/assignment-no-results.png"
                        alt="No Results Found"
                      />

                      <Text variant="createLabel" color="neutral.900">
                        {noResultsText || t('admin.assignments.noResults')}
                      </Text>
                    </VStack>
                  </Center>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {table.getPageCount() > 0 && (
        <Flex
          paddingY={2}
          paddingX={10}
          backgroundColor="neutral.0"
          position="fixed"
          bottom={0}
          left={50}
          right={0}
        >
          <ButtonGroup alignItems="center">
            <IconButton
              variant="paginationArrow"
              icon={<AngleLeft />}
              aria-label="Previous page"
              isDisabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
            />

            <HStack overflowX="scroll" maxWidth={60}>
              {pagesArr.slice(0, -1).map((_, index) => {
                // Show 5 page buttons at a time
                const currentPage = pagination.pageIndex;
                const startIndex = Math.floor(currentPage / 5) * 5;
                const endIndex = startIndex + 5;

                // Only render buttons within the current window
                if (index < startIndex || index >= endIndex) {
                  return null;
                }

                return (
                  <Button
                    variant="paginationNumber"
                    key={_.value}
                    onClick={() => table.setPageIndex(_.value)}
                    isActive={index === currentPage}
                    _active={{ borderColor: 'brand.button', color: 'baseBlack' }}
                  >
                    {_.label}
                  </Button>
                );
              })}
            </HStack>

            {pagination.pageIndex + 5 < pagesArr.length - 1 && (
              <Flex
                height="32px"
                width="32px"
                direction="column"
                alignItems="center"
                justifyContent="flex-end"
              >
                <VerticalEllipsis transform="rotate(90deg)" color="baseBlack" />
              </Flex>
            )}

            <Button
              variant="paginationNumber"
              onClick={() => table.setPageIndex(pagesArr.length - 1)}
              isActive={pagination.pageIndex === pagesArr.length - 1}
              _active={{ borderColor: 'brand.button', color: 'baseBlack' }}
            >
              {pagesArr.length}
            </Button>

            <IconButton
              variant="paginationArrow"
              icon={<AngleRight />}
              aria-label="Next page"
              isDisabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
            />
          </ButtonGroup>
        </Flex>
      )}
    </>
  );
}
